import { Injectable } from '@angular/core';
import {
  SessionService,
  isPaidUser,
  isEntitled,
} from '../services/session.service';
import { ScoreCalculationService } from '../services/score-calculation.service';
import { FullResultsData, UserData } from '../services/result.service';
import {
  DomainSummaryVM,
  ProgressCardVM,
  ProgressPoint,
  ResultsVM,
  Tone,
} from './results-vm';
import {
  higherBetterBand,
  imprintBand,
  symptomBand,
} from './results-placeholders';
import { buildImprintVM } from './panels/imprint/imprint-vm';
import { buildSymptomsVM } from './panels/symptoms/symptoms-vm';
import { buildSenseVM } from './panels/sense/sense-vm';
import { isSense2, isArchivedSense, SenseRecord } from './panels/sense/sense-model';
import { buildThinkVM, ThinkTaskInput } from './panels/think/think-vm';
import { CohortAverages } from './results-placeholders';
import { formatFirstNameForDisplay } from '../shared/name-display';
import { computeTrendStatus } from './ui/trend-status';
import { isWebDailyLoop } from '../services/daily-loop';

const TONE_CLASS: Record<Tone, DomainSummaryVM['toneClass']> = {
  green: 'tone-green',
  teal: 'tone-teal',
  amber: 'tone-amber',
  amberDeep: 'tone-amber-deep',
  red: 'tone-red',
  neutral: 'tone-amber',
};

// Placeholder series for the LOCKED progress-graph teaser on a first/single free
// scan (no real trend yet). It sits behind the blur(5px) + lock overlay, but the
// count/dates/labels are still faintly visible, so it MUST look complete like the
// design reference (markable_results_locked_proposal_he_v1: "4 בדיקות מאז 27.2",
// x-labels 27.2/10.4/22.5/9.6, deltas 2 / 7) — dated points, NOT empty dates
// (empty dates rendered a broken "מאז " + blank axis). Values mirror the ref.
const LOCKED_TEASER_POINTS: ProgressPoint[] = [
  { value: 64, date: '2026-02-27' },
  { value: 61, date: '2026-04-10' },
  { value: 59, date: '2026-05-22' },
  { value: 57, date: '2026-06-09' },
];

/**
 * Builds the typed `ResultsVM` from the live API shape + session.
 * All not-yet-real numbers come from `results-placeholders.ts` (flagged).
 */
@Injectable({ providedIn: 'root' })
export class ResultsVmService {
  /** Peritale web: no app, so the tools continue here (Danna 27.09). */
  private readonly webLoop = isWebDailyLoop();
  constructor(
    private session: SessionService,
    private scoreCalc: ScoreCalculationService
  ) {}

  build(
    scanId: string,
    full: FullResultsData,
    userData: UserData | null,
    cohort: CohortAverages | null
  ): ResultsVM {
    const firstName = formatFirstNameForDisplay(
      this.session.session?.user?.fname,
    );
    // Paywall v2 entitlement (locked-flow spec §1): an unpaid account sees the
    // four analysis domains for free but the doctor summary + progress graph
    // (and, in Option A, the routine) are locked behind the paywall.
    const sessionUser = this.session.session?.user as any;
    const isPaid = isPaidUser(sessionUser);
    // Paid-surface gating uses the UNIFIED entitlement (paid OR admin OR
    // available free-scan credit), not the narrow paid-window flag — otherwise
    // admins + admin-granted users see the doctor card / graph locked.
    const entitled = isEntitled(sessionUser);
    const toolsLocked = !!sessionUser?.freeTierToolsLocked;
    const graphLocked = !entitled;
    const score = full?.score || {};
    const perfectCorpAll = Number(score.perfectCorpAllScore) || 0;

    // Prefer the per-scan modules carried on `full` (resolved by guid, works for
    // admins and unassigned scans). Fall back to the user-scoped `user-data`
    // list only if `full` didn't carry them (older server).
    const scan = userData?.data?.find((s) => s.guid === scanId);
    const sortedScans = [...(userData?.data || [])].sort(
      (a, b) =>
        new Date(a.createdAt || 0).getTime() -
        new Date(b.createdAt || 0).getTime()
    );
    const currentScanIndex = sortedScans.findIndex((s) => s.guid === scanId);
    const checkCount =
      currentScanIndex >= 0
        ? currentScanIndex + 1
        : sortedScans.length || 1;
    const showComparisons = checkCount >= 2;
    const symptomsObj = full?.symptoms ?? scan?.symptoms ?? null;
    const senseObj = full?.sense ?? scan?.sense ?? null;
    const cognitiveSrc = full?.cognitive ?? scan?.cognitive;
    const cognitiveArr: any[] | null = Array.isArray(cognitiveSrc)
      ? cognitiveSrc
      : null;

    const imprintScore = perfectCorpAll
      ? this.scoreCalc.calculateHormonalImprintFromPerfectCorp(perfectCorpAll)
      : null;
    // Build the full symptoms VM once; its severity-tiered `score` is the single
    // source of truth for both the intro summary card and the symptom panel, so
    // the two never show different numbers for the same scan.
    // Catalog size presented in THIS scan drives the burden K (100-item sweep
    // scans -> K=33.3, legacy 60 -> K=20). Absent -> 60 (unchanged).
    const scanNActive = (full as any)?.nActive ?? (scan as any)?.nActive ?? 60;
    const symptomsVm = buildSymptomsVM(
      symptomsObj,
      cohort,
      showComparisons,
      scanNActive,
    );
    const symptomBurden = symptomsVm.score;
    const thinkScore =
      cognitiveArr && cognitiveArr.length
        ? this.scoreCalc.calculateCompositeScore(cognitiveArr)
        : null;
    // Real per-task lens scores (null tasks dropped) for the THINK deep-dive.
    const thinkTasks: ThinkTaskInput[] = (cognitiveArr || [])
      .map((t) => ({
        testId: t?.testId,
        score: this.scoreCalc.calculateSharpV2TaskScore(t),
      }))
      .filter((t): t is ThinkTaskInput => typeof t.score === 'number' && !!t.testId);
    // ---- SENSE (sense-2.0) ----
    // No score, no band. The current record plus the woman's own sense-2.0 history
    // (asc, up to and including the viewed scan) drive the change lines and the
    // two-series trend. Old sense-1.x records are archived: never read here.
    const senseCurrent: SenseRecord | null = isSense2(senseObj)
      ? (senseObj as SenseRecord)
      : null;
    const senseAvailable = senseCurrent != null;
    const senseEngineChanged = (userData?.data || []).some((s) =>
      isArchivedSense(s?.sense)
    );

    // ---- progress-over-time series (handoff #22) ----
    // Each panel plots its OWN metric across the user's history. imprint comes
    // from the per-scan Perfect Corp scores (historicalData.scores); the other
    // three are recomputed from each scan's stored module data. The last point
    // is forced to the current panel score so it matches the hero ring exactly.
    const histScores = Array.isArray(full?.historicalData?.scores)
      ? full!.historicalData!.scores
      : [];
    const imprintPoints: ProgressPoint[] = histScores
      .map((s) => ({ raw: Number(s?.perfectCorpAllScore), date: String(s?.created || '') }))
      .filter((s) => Number.isFinite(s.raw) && s.raw > 0)
      .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime())
      .map((s) => ({
        value: Math.round(this.scoreCalc.calculateHormonalImprintFromPerfectCorp(s.raw)),
        date: s.date,
      }));
    if (imprintScore != null && imprintPoints.length) {
      imprintPoints[imprintPoints.length - 1].value = imprintScore;
    }

    // Only count history up to AND INCLUDING the viewed scan — the viewed scan
    // may not be the newest (admin/older-scan view), and the trend must end on it.
    const scansUpTo =
      currentScanIndex >= 0
        ? sortedScans.slice(0, currentScanIndex + 1)
        : sortedScans;

    const symptomPoints: ProgressPoint[] = scansUpTo
      .map((s) => ({ vm: buildSymptomsVM(s?.symptoms ?? null, null, false, (s as any)?.nActive ?? 60), date: String(s?.createdAt || '') }))
      .filter((p) => typeof p.vm.score === 'number')
      .map((p) => ({ value: Math.round(p.vm.score as number), date: p.date }));
    if (symptomBurden != null && symptomPoints.length) {
      symptomPoints[symptomPoints.length - 1].value = symptomBurden;
    }

    const thinkPoints: ProgressPoint[] = scansUpTo
      .map((s) => ({ cog: Array.isArray(s?.cognitive) ? s.cognitive : null, date: String(s?.createdAt || '') }))
      .filter((s): s is { cog: any[]; date: string } => !!s.cog && s.cog.length > 0)
      .map((s) => ({ value: Math.round(this.scoreCalc.calculateCompositeScore(s.cog)), date: s.date }))
      .filter((p) => p.value > 0);
    if (thinkScore != null && thinkPoints.length) {
      thinkPoints[thinkPoints.length - 1].value = thinkScore;
    }

    // sense-2.0 records only, ascending, up to and including the viewed scan.
    let senseHist: SenseRecord[] = scansUpTo
      .map((s) => s?.sense)
      .filter((x): x is SenseRecord => isSense2(x));
    if (senseCurrent && senseHist.length === 0) senseHist = [senseCurrent];

    const domains: DomainSummaryVM[] = [
      this.scored('imprint', 'paid-1', imprintScore, imprintBand, {
        titleKey: 'results.domains.imprint.title',
      }),
      this.scored('symptoms', 'paid-2', symptomBurden, symptomBand, {
        titleKey: 'results.domains.symptoms.title',
      }),
      this.scored('think', 'paid-3', thinkScore, higherBetterBand, {
        titleKey: 'THINK™',
        titleLtr: true,
        subtitleKey: 'results.domains.think.subtitle',
      }),
      // SENSE has NO score/band (handoff 02/03). Scoreless domain card, like routine.
      {
        id: 'sense',
        panelId: 'paid-s',
        titleKey: 'SENSE™',
        titleLtr: true,
        subtitleKey: 'results.domains.sense.subtitle',
        toneClass: 'tone-neutral',
        score: null,
        max: 100,
        band: null,
        insightKey: senseAvailable
          ? 'results.domains.sense.insight'
          : 'results.domains.notCompleted',
        available: senseAvailable,
        journeyDescKey: 'results.domains.sense.journeyDesc',
        journeyStatusKey: 'results.journey.notViewed',
      },
      {
        id: 'routine',
        panelId: 'paid-0',
        titleKey: 'results.domains.routine.title',
        toneClass: 'tone-green',
        score: null,
        max: 100,
        band: null,
        insightKey: this.webLoop ? 'results.domains.routine.insightWeb' : 'results.domains.routine.insight',
        available: true,
        journeyDescKey: this.webLoop ? 'results.domains.routine.journeyDescWeb' : 'results.domains.routine.journeyDesc',
        journeyStatusKey: 'results.journey.notViewed',
      },
    ];

    return {
      scanId,
      firstName,
      createdIso: full?.created || '',
      checkCount,
      isPaid,
      isEntitled: entitled,
      toolsLocked,
      domains,
      progress: {
        // imprint + symptoms are lower-is-better → orient "better = up" (invert),
        // so an improving score rises on the chart instead of drawing a
        // down-slope that reads as decline. THINK is higher-is-better (no invert).
        imprint: this.buildProgress(imprintPoints, 'var(--brand-color)', graphLocked, true),
        symptoms: this.buildProgress(symptomPoints, '#d05e5a', graphLocked, true),
        think: this.buildProgress(thinkPoints, '#3a9e95', graphLocked, false),
        sense: null, // SENSE has its own two-series trend inside the panel (no 0-100 line)
      },
      imprint: buildImprintVM(full, imprintScore, cohort, showComparisons),
      symptoms: symptomsVm,
      sense: buildSenseVM(senseCurrent, senseHist, {
        engineChanged: senseEngineChanged,
        fmtDate: (iso) => this.fmtDM(iso),
      }),
      think: buildThinkVM(thinkScore, thinkTasks, cohort, showComparisons, {
        memoryScene:
          cognitiveArr?.find((t) => t?.testId === 'memory-scene')?.response ??
          null,
        echo:
          cognitiveArr?.find((t) => t?.testId === 'echo-chamber')?.response ??
          null,
      }, (thinkPoints?.length ?? 0) < 2),
    };
  }

  /**
   * Builds a within-person progress card from a metric's time-ordered series.
   * Returns null on a first check (<2 points) — nothing to compare yet. The
   * plotted line is capped to the last 4 points, while the baseline chip still
   * references the true first check (handoff #22, §4).
   */
  private buildProgress(
    points: ProgressPoint[],
    color: string,
    locked = false,
    lowerIsBetter = false
  ): ProgressCardVM | null {
    // A LOCKED graph is a blurred premium teaser and must appear on EVERY results
    // page — including a first/single free scan where there is no real trend yet —
    // so unpaid users always see the "unlock your progress over time" card (the
    // graphs Danna reported as "disappeared"). Feed it a placeholder series when
    // there is no genuine 2+-point history (hidden behind the blur + lock overlay).
    // A PAID scan with <2 points has a real empty trend, so it stays hidden.
    if (!points || points.length < 2) {
      if (!locked) return null;
      points = LOCKED_TEASER_POINTS;
    }
    const total = points.length;
    const current = points[total - 1].value;
    const previous = points[total - 2].value;
    const first = points[0].value;
    // orient so the arrow follows the plotted line (better = up). For a
    // lower-is-better metric a raw DROP is an improvement, so the arrow points ▲.
    const orient = lowerIsBetter ? -1 : 1;
    // Trend word/magnitude are anchored on the TRUE first scan (fixed reference,
    // spec §5), so compute the status here on the FULL uncapped series — the
    // plotted `points` is capped to the last 4, which would make the word roll
    // against the 4th-from-last scan for anyone with 5+ scans (validator bug).
    const trendStatus = computeTrendStatus(
      points.map((p) => p.value),
      lowerIsBetter
    );
    return {
      color,
      checkCount: total,
      sinceDate: this.fmtDM(points[0].date),
      points: points.slice(-4),
      trendStatus,
      deltaPrev: Math.abs(current - previous),
      deltaPrevDir: orient * Math.sign(current - previous),
      deltaBase: Math.abs(current - first),
      deltaBaseDir: orient * Math.sign(current - first),
      showBaseChip: total >= 3,
      invert: lowerIsBetter,
      locked,
    };
  }

  private fmtDM(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${d.getDate()}.${d.getMonth() + 1}`;
  }

  private scored(
    id: DomainSummaryVM['id'],
    panelId: string,
    score: number | null,
    bandFn: (s: number) => ReturnType<typeof imprintBand>,
    extra: Partial<DomainSummaryVM> & { titleKey: string }
  ): DomainSummaryVM {
    const band = score != null ? bandFn(score) : null;
    return {
      id,
      panelId,
      toneClass: band ? TONE_CLASS[band.tone] : 'tone-neutral',
      score,
      max: 100,
      band,
      // No findings copy when the module wasn't completed (would describe data
      // that doesn't exist). Show a neutral "complete this" CTA instead.
      insightKey: score != null ? `results.domains.${id}.insight` : 'results.domains.notCompleted',
      available: score != null,
      journeyDescKey: `results.domains.${id}.journeyDesc`,
      journeyStatusKey: 'results.journey.notViewed',
      titleLtr: false,
      ...extra,
    };
  }
}
